import * as d3 from 'd3'
import d3Tip from 'd3-tip'
import cloneDeep from 'lodash/cloneDeep'
import uniqueId from 'lodash/uniqueId'
import { ES } from '../locales/d3'

export default class BarChart {
  constructor(el, options) {
    this.el = el
    this._buildSvg()
    this._setupResize()
    const defaults = { duration: 750, verticalPaddingFactor: 0.1 }
    this.options = { ...defaults, ...(options || {}) }
  }

  update(data, locale) {
    this.datapoints = cloneDeep(data).datapoints
    this.yFormat = data.yFormat
    this.locale = locale
    this._draw()
  }

  destroy() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this._redrawChart)
    }

    this.mounted = false

    if (this.tip) {
      const nodes = document.querySelectorAll('.chart-tip')
      for (let i = 0; i < nodes.length; i++) {
        const el = nodes[i]
        el.parentNode.removeChild(el)
      }
      delete this.tip
    }

    if (this.svg) {
      this.svg.selectAll('g').remove()
      this.svg.selectAll('defs').remove()
    }
  }

  get xDomain() {
    return this.datapoints.map(d => d.date)
  }

  _getPaddedYDomain() {
    let min = d3.min(this.datapoints, d => d.value)
    let max = d3.max(this.datapoints, d => d.value)

    // give the chart some breathing room, make it less dramatic
    min -= max * this.options.verticalPaddingFactor
    max += max * this.options.verticalPaddingFactor

    if (min < 0) min = 0

    return [min, max]
  }

  _buildSvg() {
    this.svg = d3.select(this.el)
  }

  _buildChartContainer() {
    this.chart = this.svg
      .append('g')
      .attr('width', this.chartWidth)
      .attr('height', this.chartHeight)
      .attr('class', 'airdna bar-chart__container')
      .attr('transform', `translate(${this.margin.left}, ${this.margin.top})`)
  }

  _draw() {
    if (!this.datapoints) {
      return
    }
    this.svg.selectAll('g').remove()
    this.svg.selectAll('defs').remove()
    this._calcDimensions()
    this._buildChartContainer()
    this._calcDimensions()
    this._calcScales()
    this._setupTips()
    this._drawBars()
    this._drawXAxis()
    this._drawYAxis()
  }

  _calcDimensions() {
    this.margin = { top: 20, right: 20, bottom: 30, left: 30 }
    this.outerWidth = this.el.getBoundingClientRect().width
    this.outerHeight = this.el.getBoundingClientRect().height
    this.chartWidth = this.outerWidth - this.margin.left - this.margin.right
    this.chartHeight = this.outerHeight - this.margin.top - this.margin.bottom
  }

  _calcScales() {
    this.scaleX = d3.scaleBand().range([0, this.chartWidth]).domain(this.xDomain).round(true).padding(0.375)
    this.scaleY = d3.scaleLinear().domain(this._getPaddedYDomain()).range([this.chartHeight, 0])
  }

  _drawBars() {
    this.bar = this.chart.selectAll('rect').data(this.datapoints)

    this.bar.exit().remove()

    this.bar
      .transition()
      .duration(this.options.duration)
      .attr('x', d => this.scaleX(d.date))
      .attr('y', d => this.scaleY(d.value))
      .attr('width', this.scaleX.bandwidth())
      .attr('height', d => this.chartHeight - this.scaleY(d.value))

    this.bar
      .enter()
      .append('rect')
      .on('mouseover', this.tip.show)
      .on('mouseout', this.tip.hide)
      .attr('class', 'bar-chart__bar')
      .attr('x', d => this.scaleX(d.date))
      .attr('y', this.chartHeight)
      .transition()
      .duration(this.options.duration)
      .attr('y', d => this.scaleY(d.value))
      .attr('width', this.scaleX.bandwidth())
      .attr('height', d => this.chartHeight - this.scaleY(d.value))
  }

  _drawXAxis() {
    this.locale === 'es' && d3.timeFormatDefaultLocale(ES)

    const xAxis = d3.axisBottom(this.scaleX).tickFormat(d3.timeFormat('%b'))

    this.xAxis = this.chart
      .append('g')
      // .attr('class', 'axis axis--x')
      .attr('class', 'bar-chart__axis bar-chart__axis--x')
      .attr('transform', `translate(0, ${this.chartHeight})`)
      .call(xAxis)
  }

  _drawYAxis() {
    const format = d => (this.yFormat === 'money' ? d3.format('.2s')(d) : d)
    const prefix = this.yFormat === 'money' ? '$' : ''

    const yAxis = d3
      .axisRight(this.scaleY)
      .tickSize(this.outerWidth)
      .tickArguments([3])
      .tickFormat(d => prefix + format(d))

    this.yAxis = this.svg
      .insert('g', ':first-child')
      .attr('class', 'bar-chart__axis bar-chart__axis--y')
      .attr('transform', `translate(0, ${this.margin.top})`)

    this.yAxis.call(yAxis)

    this.yAxis
      .selectAll('text')
      .attr('x', 0)
      .attr('dy', 4) // move text down half its height to vert align center
      .attr('transform', 'translate(0, 0)')
    this.yAxis.selectAll('line').attr('transform', `translate(${this.margin.left}, 0)`)
  }

  _setupResize() {
    if (this._redrawChart) return

    this._redrawChart = () => {
      this._draw()
    }

    if (typeof window !== 'undefined') window.addEventListener('resize', this._redrawChart)
  }

  _setupTips() {
    // Workaround for tests to run
    // if (!d3.tip) {
    //   this.tip = {show: () => {}, hide: () => {}}
    //   return
    // }
    if (!this.tip) {
      const id = uniqueId('barChart')

      this.tip = d3Tip()
        .attr('class', 'chart-tip')
        .attr('id', id)
        .style('z-index', 8)
        .offset([-18, 0])
        .html(d => `${d.target['__data__'].tooltipDescription}<em>${d.target['__data__'].tooltipEmphasis}</em>`)

      this.chart.call(this.tip)
    }
  }
}

// if(typeof module !== 'undefined' && module.exports){
//   module.exports = BarChart;
// }else if(typeof window !== 'undefined'){
//   window.BarChart = BarChart;
// }
